import React from "react";
import { QRCodeSVG } from "qrcode.react";

/**
 * One printed student badge.
 *
 * This is the only place a badge is drawn. The bulk print sheet under
 * /check-in and the single-student "Print badge" action on a student profile
 * both render this component against the same stylesheet, so the card cannot
 * drift between them.
 *
 * `onToggle` / `onPrintOne` are optional: pass them for the bulk sheet, leave
 * them out for the single-student card, which has nothing to tick or narrow.
 */
export function BadgeCard({
  badge,
  selected = true,
  /** Left off the printed sheet — from the ticks, or from a solo print. */
  printSkipped = false,
  onToggle,
  onPrintOne,
}) {
  const name = `${badge.firstname || ""} ${badge.lastname || ""}`.trim();
  const interactive = typeof onToggle === "function";

  const className = [
    "badge-card",
    selected ? "" : "badge-card--off",
    printSkipped ? "badge-card--skip" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const content = (
    <>
      <div className="badge-qr">
        {/*
          The QR encodes the bare badge code and nothing else — no URL, no
          payload. Level H so a scuffed card still reads, and rendered at
          256px then scaled to 1.1in in CSS so it stays crisp at 300dpi.
          Do not shrink `size`.
        */}
        <QRCodeSVG
          value={badge.badge_code}
          size={256}
          level="H"
          marginSize={2}
          bgColor="#ffffff"
          fgColor="#000000"
        />
      </div>
      <div className="badge-body">
        <p className="badge-name">{name || "Unnamed student"}</p>
        {/*
          Badge code, then the student id on its own line. They shared one line
          at first, separated by a middot; on paper the run of tracked mono
          characters read as one long string and the id was hard to pick out.
          The card still measures 2in and the QR still sits 0.65in from the top
          — the third line is absorbed by the free space the body had beneath
          the logo, see the arithmetic in badge-sheet.css.

          The id is the portal's user_id, the only student identifier this
          system has, and it is what lets staff match a loose card back to a
          record without scanning it. Bare, with no "ID" label: nothing else on
          the card is labelled either.
        */}
        <p className="badge-code">{badge.badge_code}</p>
        {badge.user_id != null && (
          <p className="badge-id-line">{badge.user_id}</p>
        )}
        {/*
          The mono asset, not the brand-blue one, on screen as well as on
          paper: the card is a preview of the printed badge, so it should show
          what comes out of the printer. This PNG is genuinely grayscale — a
          colour logo behind a CSS filter prints as composite black and comes
          out reddish.
        */}
        <img
          className="badge-brand"
          src="/brand/logo-horizontal-black.png"
          alt="Zebra Robotics"
        />
      </div>
    </>
  );

  if (!interactive) {
    return <div className={className}>{content}</div>;
  }

  return (
    // A label so the whole card toggles its checkbox — the native control
    // carries the state; skipped cards drop out of the sheet at print.
    <label className={className}>
      <input
        type="checkbox"
        className="badge-pick zr-no-print"
        checked={selected}
        onChange={onToggle}
        aria-label={`Include ${name || "unnamed student"} on the print sheet`}
      />
      {typeof onPrintOne === "function" && (
        <button
          type="button"
          className="badge-print-one zr-no-print"
          onClick={(event) => {
            // Inside a label: don't let the click double as a tick toggle.
            event.preventDefault();
            event.stopPropagation();
            onPrintOne();
          }}
        >
          Print this one
        </button>
      )}
      {content}
    </label>
  );
}
